import { AgreementCustomerBrief } from './AgreementCustomerBrief';
import { AgreementVehiclePanel } from './AgreementVehiclePanel';
import { AgreementDealerPanel } from './AgreementDealerPanel';
import { AgreementPurchasePanel } from './AgreementPurchasePanel';
import { AuthorisedContactsPanel } from './servicing/AuthorisedContactsPanel/AuthorisedContactsPanel';
import { VehicleAmendmentLog } from './servicing/VehicleAmendmentLog/VehicleAmendmentLog';
import type { useAgreementAuthorisedContactsPanel } from '../hooks/useAgreementAuthorisedContactsPanel';
import type { useAgreementVehicleAmendments } from '../hooks/useAgreementVehicleAmendments';
import type { PurchaseView } from '../services/purchaseSnapshot';
import type { Agreement } from '../types/agreements.types';
import type { RegistrationSource } from '../hooks/useAgreementVehicleRegistration';
import type { ResolvedHolder } from '../utils/holderResolution';
import type { RegistrationView } from '../utils/registrationView';

interface Props {
    agreement: Agreement;
    purchase: PurchaseView;
    holder: ResolvedHolder;
    onViewCustomer: () => void;
    registration: RegistrationView;
    registrationSource: RegistrationSource;
    canChangeRegistration: boolean;
    onChangeRegistration: () => void;
    vehicleAmendments: ReturnType<typeof useAgreementVehicleAmendments>;
    contacts: ReturnType<typeof useAgreementAuthorisedContactsPanel>;
}

export function AgreementOverviewTab({
    agreement,
    purchase,
    holder,
    onViewCustomer,
    registration,
    registrationSource,
    canChangeRegistration,
    onChangeRegistration,
    vehicleAmendments,
    contacts,
}: Props) {
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <AgreementCustomerBrief holder={holder} onViewCustomer={onViewCustomer} />
                <AgreementVehiclePanel
                    snapshot={agreement.vehicleSnapshot}
                    registration={registration}
                    source={registrationSource}
                    canChangeRegistration={canChangeRegistration}
                    onChangeRegistration={onChangeRegistration}
                    log={
                        vehicleAmendments.canView && (
                            <VehicleAmendmentLog
                                isLoading={vehicleAmendments.amendments.query.isLoading}
                                isError={vehicleAmendments.amendments.query.isError}
                                page={vehicleAmendments.amendments.query.data}
                                currentPage={vehicleAmendments.amendments.page}
                                onRetry={() => vehicleAmendments.amendments.query.refetch()}
                                onPageChange={vehicleAmendments.amendments.setPage}
                            />
                        )
                    }
                />
            </div>
            <AgreementPurchasePanel purchase={purchase} />
            <AgreementDealerPanel dealerId={agreement.dealerId} />
            <AuthorisedContactsPanel
                agreementId={agreement.id}
                isLoading={contacts.contacts.query.isLoading}
                isError={contacts.contacts.query.isError}
                page={contacts.contacts.query.data}
                currentPage={contacts.contacts.page}
                status={agreement.status}
                canManage={contacts.canManage}
                onRetry={() => contacts.contacts.query.refetch()}
                onPageChange={contacts.contacts.setPage}
            />
        </div>
    );
}
